import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "../context/LanguageContext";
import { API, LOGO_URL } from "../config/constants";

const ForgotPasswordPage = () => {
  const [step, setStep] = useState(1);
  const [identifier, setIdentifier] = useState("");
  const [otp, setOtp] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [withdrawalPin, setWithdrawalPin] = useState("");
  const [confirmWithdrawalPin, setConfirmWithdrawalPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const { t } = useLanguage();
  const navigate = useNavigate();

  // OTP Timer countdown
  useEffect(() => {
    if (otpTimer > 0) {
      const interval = setInterval(() => {
        setOtpTimer(prev => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [otpTimer]);

  const sendResetOTP = async () => {
    if (!identifier) { toast.error("Enter phone or email"); return; }
    if (otpTimer > 0) { toast.error(`Please wait ${otpTimer} seconds`); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { identifier });
      setStep(2);
      setOtpTimer(60); // Start 60 second countdown
      toast.success("OTP sent to registered email");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async () => {
    if (newPin.length !== 8 || !/^\d{8}$/.test(newPin)) { 
      toast.error("Login PIN must be exactly 8 digits"); 
      return; 
    }
    if (newPin !== confirmPin) { 
      toast.error("Login PINs don't match"); 
      return; 
    }
    if (withdrawalPin.length !== 6 || !/^\d{6}$/.test(withdrawalPin)) { 
      toast.error("Withdrawal PIN must be exactly 6 digits"); 
      return; 
    }
    if (withdrawalPin !== confirmWithdrawalPin) { 
      toast.error("Withdrawal PINs don't match"); 
      return; 
    }
    if (newPin.includes(withdrawalPin) || newPin.substring(0, 6) === withdrawalPin || newPin.substring(2) === withdrawalPin) {
      toast.error("Withdrawal PIN must be different from Login PIN");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { 
        identifier, 
        otp, 
        new_pin: newPin,
        withdrawal_pin: withdrawalPin 
      });
      toast.success(t("passwordResetSuccess"));
      navigate("/");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-start pt-8 px-4" data-testid="forgot-password-page">
      <img src={LOGO_URL} alt="Lite Gold" className="w-16 h-16 rounded-full" />
      <h1 className="text-gold-gradient font-manrope text-2xl font-bold mt-2">{t("resetPassword")}</h1>

      <div className="w-full max-w-md mt-6 bg-card border border-border rounded-2xl p-6">
        {step === 1 && (
          <>
            <input type="text" placeholder={t("phoneOrEmail")} value={identifier} onChange={(e) => setIdentifier(e.target.value)} className="input-dark mb-4" data-testid="forgot-identifier-input" />
            <button onClick={sendResetOTP} disabled={loading} className="w-full btn-gold py-3 rounded-full" data-testid="forgot-send-otp-btn">
              {loading ? "..." : t("sendOTP")}
            </button>
          </>
        )}
        {step === 2 && (
          <>
            <input type="text" placeholder={t("enterOTP")} value={otp} onChange={(e) => setOtp(e.target.value)} maxLength={6} className="input-dark mb-3" />
            
            <p className="text-primary text-xs font-bold mb-2">🔐 New Login PIN (8 digits)</p>
            <input type="password" placeholder="New 8-digit Login PIN" value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))} maxLength={8} className="input-dark mb-2" />
            <p className="text-muted-foreground text-xs mb-2">{newPin.length}/8 digits</p>
            <input type="password" placeholder="Confirm Login PIN" value={confirmPin} onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ""))} maxLength={8} className="input-dark mb-3" />
            
            <p className="text-orange-400 text-xs font-bold mb-2">💰 New Withdrawal PIN (6 digits)</p>
            <input type="password" placeholder="New 6-digit Withdrawal PIN" value={withdrawalPin} onChange={(e) => setWithdrawalPin(e.target.value.replace(/\D/g, ""))} maxLength={6} className="input-dark mb-2" />
            <p className="text-muted-foreground text-xs mb-2">{withdrawalPin.length}/6 digits</p>
            <input type="password" placeholder="Confirm Withdrawal PIN" value={confirmWithdrawalPin} onChange={(e) => setConfirmWithdrawalPin(e.target.value.replace(/\D/g, ""))} maxLength={6} className="input-dark mb-4" />
            
            <button onClick={resetPassword} disabled={loading} className="w-full btn-gold py-3 rounded-full" data-testid="forgot-reset-btn">
              {loading ? "..." : "Reset Both PINs"}
            </button>
            {/* Resend OTP Button */}
            <button 
              type="button"
              onClick={sendResetOTP} 
              disabled={otpTimer > 0 || loading}
              className={`w-full mt-3 py-2 rounded-full text-sm ${otpTimer > 0 ? 'bg-gray-700 text-gray-400' : 'bg-card text-primary border border-primary'}`}
            >
              {otpTimer > 0 ? `Resend OTP in ${otpTimer}s` : "Resend OTP"}
            </button>
          </>
        )}
        <button onClick={() => navigate("/")} className="w-full text-center text-muted-foreground mt-4">{t("login")}</button>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
